import { type FieldValues, useForm } from "react-hook-form";
import {
  useAdditionalInfoFormFields,
  type AdditionalInfoFormContext,
  type DriverLicenseFormContext,
  useDriverLicenseFormFields,
  useFrequentFlyerFormFields,
  type FrequentFlyerFormContext,
  type FrequentFlyerList,
  type PassengerFormContext,
  type TravellerDetailsFormContext,
  TravelerEditMode,
  ConvertFormToTraveler,
} from "@apac/travelers";
import type { Traveler } from "@b2bportal/traveler-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { usePassportFormFields } from "./usePassportFormFields";
import { useTravellerDetailsFormFields } from "./useTravellerDetailsFormFields";
import type { ActivityFieldValues } from "@/modules/activities-checkout/components/ActivityDetails";

export interface ActivityFormContext {
  height?: string;
  weight?: string;
}

export type TravellerFormValues = TravellerDetailsFormContext &
  PassengerFormContext &
  FrequentFlyerFormContext &
  AdditionalInfoFormContext &
  DriverLicenseFormContext &
  ActivityFormContext;

export interface UseTravellerFormParams {
  traveler?: Traveler;
  dateFormat?: string;
  showPassportDetails?: boolean;
  showGender?: boolean;
  showNationality?: boolean;
  onSubmit: (traveller: Traveler) => void;
  onActivityFieldsSubmit?: (
    travelerId: string,
    activityFields: ActivityFieldValues
  ) => void;
  travelerEditMode?: TravelerEditMode;
  showDriverLicenseDetails?: {
    showAddress: boolean;
    showCity: boolean;
  };
  travelerBookingQuestions?: Array<{
    id: string;
    label: string;
    required: string;
  }>;
  missingPassportInfoError?: boolean;
  requireDriverLicense?: boolean;
}

export interface UseTravellerFormResult {
  context: {
    formContext: ReturnType<typeof useForm<TravellerFormValues>>;
    frequentFlyerList: FrequentFlyerList;
    existingPassenger: boolean;
  };
  handlers: {
    onSubmitTraveller: () => void;
    onFrequentFlyerSubmit: (data: {
      frequentFlyerProgram: string;
      frequentFlyerNumber: string;
    }) => void;
    onRemoveFrequentFlyer: (index: number) => void;
  };
}

export const useTravellerForm = ({
  traveler,
  onSubmit,
  onActivityFieldsSubmit,
  dateFormat,
  showPassportDetails,
  showGender = true,
  showNationality = false,
  travelerEditMode = TravelerEditMode.ALL,
  showDriverLicenseDetails = {
    showAddress: false,
    showCity: false,
  },
  travelerBookingQuestions,
  missingPassportInfoError = false,
  requireDriverLicense = true,
}: UseTravellerFormParams): UseTravellerFormResult => {
  const { t } = useI18nContext();
  const { formFields: travelerFormFields, travellerDetailsDefaultValues } =
    useTravellerDetailsFormFields({
      dateFormat,
      showGender,
      travelerEditMode,
      showNationality,
    });

  // Check if passport is required via booking questions (activities flow)
  const hasRequiredPassportQuestions = travelerBookingQuestions?.some(
    (q) =>
      (q.id === "PassportPassportNo" ||
        q.id === "PassportExpiry" ||
        q.id === "PassportNationality") &&
      q.required === "Mandatory"
  );

  // Passport is required if EITHER:
  // 1. Booking questions require it (activities), OR
  // 2. Missing passport info error is true (flights/packages when passport is missing)
  const requirePassport =
    hasRequiredPassportQuestions || missingPassportInfoError;

  const { formFields: passportFormFields, passportDetailsDefaultValues } =
    usePassportFormFields({
      dateFormat,
      traveler,
      requirePassport,
    });

  const {
    formFields: licenseFields,
    driverLicenseDetailsDefaultValues: licenseDefaultValues,
  } = useDriverLicenseFormFields({ ...showDriverLicenseDetails });

  // For packages, remove required validation from driver license fields
  const driverLicenseFields = requireDriverLicense
    ? licenseFields
    : Object.fromEntries(
        Object.entries(licenseFields).map(([key, field]) => [
          key,
          { ...field, required: false },
        ])
      );

  const {
    frequentFlyerList,
    onFrequentFlyerSubmit,
    onRemoveFrequentFlyer,
    formFields: frequentFlyerFormFields,
    frequentFlyerDetailsDefaultValues,
  } = useFrequentFlyerFormFields({ traveler });

  const { formFields: additionalInfoFormFields, additionalInfoDefaultValues } =
    useAdditionalInfoFormFields();

  const showDriverInfo =
    travelerEditMode === TravelerEditMode.CARS_ONLY ||
    travelerEditMode === TravelerEditMode.ALL;
  const showFlightSections =
    travelerEditMode === TravelerEditMode.FLIGHTS_ONLY ||
    travelerEditMode === TravelerEditMode.ALL;
  const showActivitiesInfo =
    travelerEditMode === TravelerEditMode.ACTIVITIES_ONLY ||
    travelerEditMode === TravelerEditMode.ALL;

  const activityFormFields: Record<string, FieldValues> = {};
  const activityDefaultValues: ActivityFormContext = {};

  if (showActivitiesInfo && travelerBookingQuestions?.length) {
    const heightQuestion = travelerBookingQuestions.find(
      (q) => q.id === "Height"
    );
    const weightQuestion = travelerBookingQuestions.find(
      (q) => q.id === "Weight"
    );

    if (heightQuestion) {
      activityDefaultValues.height = "";
      if (heightQuestion.required === "Mandatory") {
        activityFormFields.height = {
          required: t("travelerForm.activityDetails.heightRequired"),
        };
      }
    }

    if (weightQuestion) {
      activityDefaultValues.weight = "";
      if (weightQuestion.required === "Mandatory") {
        activityFormFields.weight = {
          required: t("travelerForm.activityDetails.weightRequired"),
        };
      }
    }
  }

  const initialFormValues = ConvertFormToTraveler.getTravellerFormValues({
    traveler,
    dateFormat,
    defaultValues: {
      ...travellerDetailsDefaultValues,
      ...(showPassportDetails ? passportDetailsDefaultValues : {}),
      ...(showDriverInfo ? licenseDefaultValues : {}),
      ...(showFlightSections ? frequentFlyerDetailsDefaultValues : {}),
      ...(showFlightSections ? additionalInfoDefaultValues : {}),
      ...(showActivitiesInfo ? activityDefaultValues : {}),
    },
  });

  const formContext = useForm<TravellerFormValues>({
    defaultValues: initialFormValues,
    mode: "onBlur",
  });

  for (const [name, details] of Object.entries({
    ...travelerFormFields,
    ...(showPassportDetails ? passportFormFields : {}),
    ...(showDriverInfo ? driverLicenseFields : {}),
    ...(showFlightSections ? frequentFlyerFormFields : {}),
    ...(showFlightSections ? additionalInfoFormFields : {}),
    ...(showActivitiesInfo ? activityFormFields : {}),
  })) {
    formContext.register(
      name as keyof TravellerFormValues,
      details as FieldValues
    );
  }

  const onSubmitTraveller = formContext.handleSubmit(() => {
    const formValues = formContext.getValues();
    const newTraveler = ConvertFormToTraveler.convertFormToTraveler({
      traveler: traveler || {},
      formValues,
      frequentFlyerList,
      dateFormat,
      travelerEditMode,
    });

    onSubmit(newTraveler);

    if (onActivityFieldsSubmit && showActivitiesInfo) {
      const activityFields: ActivityFieldValues = {};
      if (formValues.height) {
        activityFields.height = formValues.height as string;
      }
      if (formValues.weight) {
        activityFields.weight = formValues.weight as string;
      }
      if (activityFields.height || activityFields.weight) {
        onActivityFieldsSubmit(newTraveler.id, activityFields);
      }
    }
  });

  return {
    context: {
      formContext,
      frequentFlyerList,
      existingPassenger: !!traveler?.id,
    },
    handlers: {
      onSubmitTraveller,
      onFrequentFlyerSubmit,
      onRemoveFrequentFlyer,
    },
  };
};
